import { type FormEvent, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router'
import DownloadIcon from '@mui/icons-material/Download'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { RegisteredSshPublicKey, SshCertificate } from 'common-billing-server/types'

import { useBillingApi } from '../../api/billing/billing-context'
import { Path } from '../../routes/path'
import type { FailedOperation } from '../common/hooks/hook-failed-operation'

interface SshAccessDialogProps {
    open: boolean
    /** Issues the certificate for this serial. Without it the dialog asks for one, listed here or not. */
    serialNumber?: string
    onClose: () => void
    /** Called once the certificate has been downloaded and the dialog has closed. */
    onIssued: (certificate: SshCertificate) => void
    onFailed: (operation: FailedOperation) => void
}

/**
 * Downloads an issued SSH certificate using the filename returned by Billing Server.
 * @param certificate - Certificate response returned by Billing Server
 */
export const downloadSshCertificate = (certificate: SshCertificate) => {
    const blob = new Blob([`${certificate.signedKey}\n`], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = certificate.fileName
    link.click()
    URL.revokeObjectURL(url)
}

/**
 * Issues an SSH certificate for the caller's registered public key. With a fixed serial it serves
 * the appliance details page; without one it lets support reach appliances that are not listed in
 * Billing Server, such as Edge Core cloud VMs without a license.
 */
export const SshAccessDialog = ({ open, serialNumber, onClose, onIssued, onFailed }: SshAccessDialogProps) => {
    const billingApi = useBillingApi()
    const [sshKey, setSshKey] = useState<RegisteredSshPublicKey | null>()
    const [enteredSerial, setEnteredSerial] = useState('')
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open) return
        let cancelled = false
        setSshKey(undefined)
        setEnteredSerial('')
        billingApi.getCurrentUserSshKey().then(
            (result) => {
                if (!cancelled) setSshKey(result.sshKey)
            },
            (error: Error) => {
                if (cancelled) return
                onClose()
                onFailed({ id: 'load-ssh-key', message: 'Your SSH public key could not be loaded.', error })
            }
        )
        return () => {
            cancelled = true
        }
        // The callbacks are recreated by the parent on each render; listing them would reload the key forever
    }, [open, billingApi])

    const targetSerial = (serialNumber ?? enteredSerial).trim()
    const canRequest = !!sshKey && !!targetSerial && !loading

    const requestCertificate = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!canRequest) return
        setLoading(true)
        try {
            const certificate = await billingApi.sshCertificateAppliance(encodeURIComponent(targetSerial))
            downloadSshCertificate(certificate)
            onClose()
            onIssued(certificate)
        } catch (error) {
            onClose()
            onFailed({
                id: 'ssh-certificate',
                message: 'The SSH certificate could not be issued.',
                error: error as Error,
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: { component: 'form', onSubmit: requestCertificate, sx: { maxWidth: 520 } },
            }}
        >
            <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1, fontSize: '1.25rem', lineHeight: 1.4, fontWeight: 600 }}>
                {serialNumber ? `SSH access to ${serialNumber}` : 'SSH access by serial number'}
            </DialogTitle>
            <DialogContent sx={{ px: 3, pt: '4px !important', pb: 0 }}>
                {sshKey === undefined ? (
                    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 120 }}>
                        <CircularProgress aria-label="Loading SSH public key" />
                    </Box>
                ) : sshKey ? (
                    <Stack spacing={1.5}>
                        {serialNumber ? (
                            <Typography>
                                Download a certificate for this appliance. It is valid for 12 hours.
                            </Typography>
                        ) : (
                            <>
                                <Typography>
                                    Download a certificate for an appliance by its serial number, whether or not it is
                                    listed here. This also covers Edge Core cloud VMs without a license. The certificate
                                    is valid for 12 hours.
                                </Typography>
                                <TextField
                                    label="Serial number"
                                    value={enteredSerial}
                                    onChange={(event) => setEnteredSerial(event.target.value)}
                                    autoFocus
                                    required
                                    fullWidth
                                    disabled={loading}
                                    slotProps={{ htmlInput: { 'data-testid': 'ssh-access-serial' } }}
                                />
                            </>
                        )}
                        <Typography>
                            Place the downloaded <code>*-cert.pub</code> file beside the matching private key, then
                            connect normally with SSH.
                        </Typography>
                    </Stack>
                ) : (
                    <Typography>Add an SSH public key to your profile before requesting access.</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pt: 2, pb: 2.5 }}>
                <Button onClick={onClose} disabled={loading}>
                    Close
                </Button>
                {sshKey === null && (
                    <Button component={RouterLink} to={Path.profile} variant="contained" onClick={onClose}>
                        Open profile
                    </Button>
                )}
                {sshKey && (
                    <Button type="submit" variant="contained" startIcon={<DownloadIcon />} disabled={!canRequest}>
                        {loading ? 'Downloading…' : 'Download certificate'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
